/**
 * ตัวจริงของ CrmApiPort ฝั่ง ishealth-platform — ห่อ issara-sdk
 *
 * ไฟล์นี้เป็น **ไฟล์เดียวในระบบ** ที่เชื่อมโมดูล crm-backoffice เข้ากับ issara-sdk
 * โมดูลนั้นไม่ import issara-sdk เลยโดยตั้งใจ เพราะไฟล์ generated ทุกไฟล์
 * `import config from "../../config/config"` ซึ่งเป็นไฟล์ของ ishealth-platform เอง
 * ไม่ใช่ submodule ถ้าโมดูลอ้างถึงตรง ๆ โหมด standalone ของมันจะพังทันที
 *
 * หน้าที่ของไฟล์นี้มีสามอย่างเท่านั้น
 *   1. แปลง tuple [data, error, network] ของ issara-sdk เป็น Promise + throw
 *   2. แปลง snake_case ↔ camelCase (issara-sdk ไม่แปลงให้ ต่างจาก http.ts ของโมดูล)
 *   3. ใส่ apiToken และ division ให้ทุก request
 *
 * ⛔ ห้ามใส่ตรรกะทางธุรกิจในไฟล์นี้ — ถ้าต้องแปลงข้อมูล ให้ไปทำที่ adapters.ts ของโมดูล
 */

import type { CrmApiPort } from "crm-backoffice/integration";
import { ApiError, camelize, snakeize } from "crm-backoffice/integration";

import LoyaltyAccountDetailView from "issara-sdk/apis/LoyaltyAccountDetailView_apps_CRM";
import LoyaltyAccountListView from "issara-sdk/apis/LoyaltyAccountListView_apps_CRM";
import LoyaltyAccountTierSummaryView from "issara-sdk/apis/LoyaltyAccountTierSummaryView_apps_CRM";
import AuditLogEarningPolicyListView from "issara-sdk/apis/AuditLogEarningPolicyListView_apps_CRM";
import AuditLogLoyaltyRewardListView from "issara-sdk/apis/AuditLogLoyaltyRewardListView_apps_CRM";
import AuditLogLoyaltyTierBenefitCodeListView from "issara-sdk/apis/AuditLogLoyaltyTierBenefitCodeListView_apps_CRM";
import AuditLogLoyaltyTierListView from "issara-sdk/apis/AuditLogLoyaltyTierListView_apps_CRM";
import LoyaltyBenefitCodeDetailView from "issara-sdk/apis/LoyaltyBenefitCodeDetailView_apps_CRM";
import LoyaltyBenefitCodeForAdminDetailView from "issara-sdk/apis/LoyaltyBenefitCodeForAdminDetailView_apps_CRM";
import LoyaltyBenefitCodeForAdminListView from "issara-sdk/apis/LoyaltyBenefitCodeForAdminListView_apps_CRM";
import LoyaltyBenefitCodeForAdminSummaryView from "issara-sdk/apis/LoyaltyBenefitCodeForAdminSummaryView_apps_CRM";
import LoyaltyBenefitCodeListView from "issara-sdk/apis/LoyaltyBenefitCodeListView_apps_CRM";
import LoyaltyTierBenefitDetailView from "issara-sdk/apis/LoyaltyTierBenefitDetailView_apps_CRM";
import LoyaltyTierBenefitListView from "issara-sdk/apis/LoyaltyTierBenefitListView_apps_CRM";
import LoyaltyEarningPolicyDetailView from "issara-sdk/apis/LoyaltyEarningPolicyDetailView_apps_CRM";
import LoyaltyEarningPolicyListView from "issara-sdk/apis/LoyaltyEarningPolicyListView_apps_CRM";
import LoyaltyRewardDetailView from "issara-sdk/apis/LoyaltyRewardDetailView_apps_CRM";
import LoyaltyRewardListView from "issara-sdk/apis/LoyaltyRewardListView_apps_CRM";
import LoyaltyRewardPointSummaryView from "issara-sdk/apis/LoyaltyRewardPointSummaryView_apps_CRM";
import LoyaltyTierDetailView from "issara-sdk/apis/LoyaltyTierDetailView_apps_CRM";
import LoyaltyTierListView from "issara-sdk/apis/LoyaltyTierListView_apps_CRM";
import PointClaimActionLogListView from "issara-sdk/apis/PointClaimActionLogListView_apps_CRM";
import PointClaimDetailView from "issara-sdk/apis/PointClaimDetailView_apps_CRM";
import PointClaimListView from "issara-sdk/apis/PointClaimListView_apps_CRM";

/* คูปองอยู่ใน BIL module ไม่ใช่ CRM — SDK generate มาเมื่อ 18 ก.ย. 2569 */
import VoucherTemplateDetailView from "issara-sdk/apis/VoucherTemplateDetailView_apps_BIL";
import VoucherTemplateListView from "issara-sdk/apis/VoucherTemplateListView_apps_BIL";
import VoucherTemplateSummaryView from "issara-sdk/apis/VoucherTemplateSummaryView_apps_BIL";

/** ข้อความไทยจาก error ของ DRF — รูปแบบเดียวกับที่โมดูลใช้ในโหมด standalone */
const messageOf = (error: any, status: number): string => {
  if (typeof error === "string") return error;
  if (typeof error?.detail === "string") return error.detail;

  const first = Object.values(error ?? {}).find((v) => Array.isArray(v)) as string[] | undefined;
  if (first?.[0]) return String(first[0]);

  if (status === 401) return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  if (status === 403) return "บัญชีนี้ไม่มีสิทธิ์ทำรายการนี้";
  if (status === 404) return "ไม่พบข้อมูลที่ต้องการ";
  if (status >= 500) return "ระบบปลายทางขัดข้อง กรุณาลองใหม่อีกครั้ง";
  return "ทำรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
};

/**
 * tuple → Promise
 *
 * ⚠️ issara-sdk คืน [data, error, network] เสมอ แม้ตอนสำเร็จ error ก็เป็น null
 *    ห้ามเช็คด้วย `if (!data)` เพราะ 204 คืน data เป็น undefined ทั้งที่สำเร็จ
 */
const unwrap = <T,>([data, error, network]: [any, any, any]): T => {
  if (error) throw new ApiError(network?.response?.status ?? 0, messageOf(error, network?.response?.status ?? 0));
  return camelize(data) as T;
};

export interface HostCrmApiConfig {
  apiToken: string;
  divisionId: number;
}

/**
 * ⚠️ ต้อง memo ไว้ที่ระดับ class ของ MainHIS
 *    ถ้าสร้างใหม่ทุก render useEffect ในโมดูลที่ผูกกับ `api` จะยิงซ้ำไม่รู้จบ
 */
export function makeHostCrmApi({ apiToken, divisionId }: HostCrmApiConfig): CrmApiPort {
  const extra = { division: divisionId };
  const auth = { apiToken, extra };

  return {
    /* ── คิวตรวจคำขอสะสมคะแนน ── */
    listClaims: (query = {}) =>
      PointClaimListView.list({ ...auth, params: snakeize(query) }).then(unwrap),

    getClaim: (id) => PointClaimDetailView.retrieve({ ...auth, pk: id }).then(unwrap),

    actOnClaim: (id, action, opts = {}) => {
      const data: Record<string, unknown> = { action };
      /* อนุมัติโดยไม่ส่ง amount = 400 "Amount is required" */
      if (opts.amount !== undefined) data.amount = opts.amount.toFixed(2);
      if (opts.rejectedReason !== undefined) data.rejected_reason = opts.rejectedReason;
      return PointClaimDetailView.patch({ ...auth, pk: id, data }).then(unwrap);
    },

    listClaimActionLogs: (limit = 500) =>
      PointClaimActionLogListView.list({ ...auth, params: { limit, ordering: "-created" } }).then(unwrap),

    /* ── สมาชิก ── */
    listAccounts: (limit = 500) =>
      LoyaltyAccountListView.list({ ...auth, params: { limit } }).then(unwrap),

    getAccount: (id) => LoyaltyAccountDetailView.retrieve({ ...auth, pk: id }).then(unwrap),

    /* ── ระดับสมาชิกและสิทธิประโยชน์ ── */
    listTiers: () =>
      LoyaltyTierListView.list({ ...auth, params: { ordering: "start_point", limit: 100 } }).then(unwrap),

    createTier: (name, startPoint) =>
      LoyaltyTierListView.create({ ...auth, data: { name, start_point: startPoint } as any }).then(unwrap),

    updateTier: (id, patch) =>
      LoyaltyTierDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    deleteTier: (id) => LoyaltyTierDetailView.delete({ ...auth, pk: id }).then(unwrap),

    listBenefitCodes: () =>
      LoyaltyBenefitCodeListView.list({ ...auth, params: { ordering: "display_seq", limit: 100 } }).then(unwrap),

    createBenefitCode: (body) =>
      LoyaltyBenefitCodeListView.create({ ...auth, data: snakeize(body) as any }).then(unwrap),

    updateBenefitCode: (id, patch) =>
      LoyaltyBenefitCodeDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    deleteBenefitCode: (id) => LoyaltyBenefitCodeDetailView.delete({ ...auth, pk: id }).then(unwrap),

    listTierSummary: () => LoyaltyAccountTierSummaryView.list({ ...auth }).then(unwrap),

    /* ── สิทธิประโยชน์ที่ผูกกับระดับสมาชิก ──
       endpoint ซ้อน: /loyalty-tier/<policy_id>/benefit-code/
       ทำให้ผูก/ถอด/แก้เพดานสิทธิ์ผ่าน API ได้ ไม่ต้องเข้า Django admin */
    listTierBenefits: (policyId) =>
      LoyaltyTierBenefitListView.list({ ...auth, policy_id: policyId, params: { limit: 200 } }).then(unwrap),

    linkTierBenefit: (policyId, body) =>
      LoyaltyTierBenefitListView.create({ ...auth, policy_id: policyId, data: snakeize(body) as any }).then(unwrap),

    updateTierBenefit: (policyId, id, patch) =>
      LoyaltyTierBenefitDetailView.patch({
        ...auth, policy_id: policyId, pk: id, data: snakeize(patch),
      }).then(unwrap),

    unlinkTierBenefit: (policyId, id) =>
      LoyaltyTierBenefitDetailView.delete({ ...auth, policy_id: policyId, pk: id }).then(unwrap),

    /* ── กติกาคะแนน ── */
    listEarningPolicies: () =>
      LoyaltyEarningPolicyListView.list({ ...auth, params: { limit: 100 } }).then(unwrap),

    updateEarningPolicy: (id, patch) =>
      LoyaltyEarningPolicyDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    /* ── ของรางวัล ── */
    listRewards: (limit = 200) =>
      LoyaltyRewardListView.list({ ...auth, params: { limit, ordering: "points" } }).then(unwrap),

    createReward: (body) =>
      LoyaltyRewardListView.create({ ...auth, data: snakeize(body) as any }).then(unwrap),

    updateReward: (id, patch) =>
      LoyaltyRewardDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    deleteReward: (id) => LoyaltyRewardDetailView.delete({ ...auth, pk: id }).then(unwrap),

    /**
     * อัปโหลดรูปของรางวัล — ส่งเป็น FormData ไม่ใช่ JSON
     *
     * ⚠️ ห้าม snakeize ค่า FormData และห้ามตั้ง Content-Type เอง
     *    axios ต้องเป็นคนเติม boundary ให้ ไม่งั้นฝั่ง backend แยกไฟล์ออกจาก body ไม่ได้
     */
    uploadRewardImage: (id, file) => {
      const form = new FormData();
      form.append("image", file);
      return LoyaltyRewardDetailView.patch({ ...auth, pk: id, data: form as any }).then(unwrap);
    },

    /* ── สรุปคะแนน ── */
    getPointSummary: (expireDays = 90) =>
      LoyaltyRewardPointSummaryView.retrieve({ ...auth, params: { expire_days: expireDays } }).then(unwrap),

    /* ── สิทธิพิเศษแบบกดรับ ── */
    listPrivileges: (limit = 200) =>
      LoyaltyBenefitCodeForAdminListView.list({ ...auth, params: { limit } }).then(unwrap),

    getPrivilegeSummary: () =>
      LoyaltyBenefitCodeForAdminSummaryView.retrieve({ ...auth }).then(unwrap),

    createPrivilege: (body) =>
      LoyaltyBenefitCodeForAdminListView.create({ ...auth, data: snakeize(body) as any }).then(unwrap),

    updatePrivilege: (id, patch) =>
      LoyaltyBenefitCodeForAdminDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    deletePrivilege: (id) => LoyaltyBenefitCodeForAdminDetailView.delete({ ...auth, pk: id }).then(unwrap),

    uploadPrivilegeImage: (id, file) => {
      const form = new FormData();
      form.append("image", file);
      return LoyaltyBenefitCodeForAdminDetailView.patch({ ...auth, pk: id, data: form as any }).then(unwrap);
    },

    /* ── ประวัติการแก้ไข ── */
    listAuditLogs: (kind, limit = 50) => {
      const view = {
        tier: AuditLogLoyaltyTierListView,
        "tier-benefit-code": AuditLogLoyaltyTierBenefitCodeListView,
        "earning-policy": AuditLogEarningPolicyListView,
        reward: AuditLogLoyaltyRewardListView,
      }[kind];

      return view.list({ ...auth, params: { limit, ordering: "-created" } }).then(unwrap);
    },

    /* ── คูปอง (BIL) ──
       ⛔ ไม่มี delete — view ฝั่ง backend เป็น RetrieveUpdateAPIView
          การ "ลบ" คูปองทำโดยตั้ง status = CANCELED */
    listVoucherTemplates: (limit = 200) =>
      VoucherTemplateListView.list({ ...auth, params: { limit } }).then(unwrap),

    /* SDK generate post/put/patch ให้ด้วยเพราะเป็น APIView แต่ backend ทำแค่ GET */
    getVoucherTemplateSummary: () => VoucherTemplateSummaryView.get({ ...auth }).then(unwrap),

    createVoucherTemplate: (body) =>
      VoucherTemplateListView.create({ ...auth, data: snakeize(body) as any }).then(unwrap),

    updateVoucherTemplate: (id, patch) =>
      VoucherTemplateDetailView.patch({ ...auth, pk: id, data: snakeize(patch) }).then(unwrap),

    uploadVoucherTemplateImage: (id, file) => {
      const form = new FormData();
      form.append("image", file);
      return VoucherTemplateDetailView.patch({ ...auth, pk: id, data: form as any }).then(unwrap);
    },
  };
}
